import React from 'react';
import PropTypes from 'prop-types';
import { gql } from '@common/hooks';
import { Colors, Spacing } from 'works/legacy/lib/style-helper';
import {
  CommentInline,
  ShowComments,
  TextOverflow,
  TimeWithTimeZone,
} from '@common/components';

const stateLabel = {
  application: {
    submittal_packet_change_requested: 'Change Requested',
    submitted: 'Submitted',
    in_review: 'In Review',
    accepted: 'Accepted',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
  },
  offer: {
    offered: 'Offer',
    retracted: 'Offer Retracted',
    rejected: 'Offer Rejected',
    change_requested: 'Offer Change Requested',
  },
};

const stateStyleMap = {
  application: {
    submittal_packet_change_requested: 'submitted',
    submitted: 'submitted',
    in_review: 'in-review',
    accepted: 'accepted-type',
    withdrawn: 'withdrawn-type',
    rejected: 'reject-type',
  },
  offer: {
    offered: 'offer-offered-type',
    retracted: 'offer-retracted-type',
    rejected: 'offer-rejected-type',
    change_requested: 'offer-change-requested-type',
  },
};

export default function StateAreaHeader({ application, offer }) {
  let stateClassName;
  let stateTextLabel;

  if (offer && application.state === 'offer') {
    stateClassName = stateStyleMap.offer[offer.state];
    stateTextLabel = stateLabel.offer[offer.state];
  } else {
    stateClassName = stateStyleMap.application[application.state];
    stateTextLabel = stateLabel.application[application.state];
  }

  if (application.doNotRehire && stateTextLabel !== 'Accepted') {
    stateClassName = `${stateClassName} do_not_rehire`;
    stateTextLabel = `${stateTextLabel} [Do Not Rehire]`;
  }

  return (
    <div>
      <div className={`status ${stateClassName}`}>{stateTextLabel}</div>
      {application.state === 'rejected' && (
        <>
          {application.rejectionReasonDescription && (
            <span className="subtext color-coral">
              {application.rejectionReasonDescription}
            </span>
          )}
          {application.rejectionDetails && (
            <span className="subtext">{application.rejectionDetails}</span>
          )}
        </>
      )}

      {application.doNotRehireComment && (
        <TextOverflow className="subtext">
          {`Supplier's note: "${application.doNotRehireComment}"`}
        </TextOverflow>
      )}

      {offer && (
        <ShowComments
          showMode="last"
          commentable={offer}
          render={(comment) => (
            <CommentInline comment={comment} prefix="Offer: " />
          )}
        />
      )}

      <span className="subtext date-time">
        <TimeWithTimeZone
          date={application.stateChangedAt}
          variant="long_format"
        />
      </span>
      <style jsx>{`
        .status {
          font-size: 20px;
          font-weight: 500;
          line-height: 1.5;
          margin-bottom: ${Spacing.baseSpacing / 4}px;
        }
        .subtext {
          display: block;
          line-height: 24px;
          font-size: 16px;
          font-weight: 300;
        }
        .date-time {
          color: ${Colors.midnightLighter};
          margin-bottom: ${Spacing.baseSpacing / 4}px;
          letter-spacing: 1px;
        }
        .submitted {
          color: ${Colors.hypnotiqRegular};
        }
        .in-review,
        .offer-retracted-type {
          color: ${Colors.harvestGold};
        }
        .offer-offered-type,
        .accepted-type,
        .offer-change-requested-type {
          color: ${Colors.grass};
        }
        .hired-type {
          color: ${Colors.grassDarkest};
        }
        .reject-type,
        .offer-rejected-type {
          color: ${Colors.coral};
        }
        .withdrawn-type {
          color: ${Colors.sunDarker};
        }
        .do_not_rehire {
          color: ${Colors.coral};
        }
      `}</style>
    </div>
  );
}

StateAreaHeader.propTypes = {
  application: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    stateChangedAt: PropTypes.string.isRequired,
    rejectionReasonDescription: PropTypes.string,
    rejectionDetails: PropTypes.string,
    doNotRehire: PropTypes.bool,
    doNotRehireComment: PropTypes.string,
  }),
  offer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    stateChangedAt: PropTypes.string.isRequired,
  }),
};

StateAreaHeader.fragments = {
  ApplicationStateAreaHeaderInfo: gql`
    fragment ApplicationStateAreaHeaderInfo on StaffingRequestApplication {
      id
      state
      stateChangedAt
      rejectionReasonDescription
    }
  `,
  OfferStateAreaHeaderInfo: gql`
    fragment OfferStateAreaHeaderInfo on Offer {
      id
      state
      stateChangedAt
    }
  `,
};
